import { ClientApiError } from './api'

/** Strip technical/internal error text before showing clients. */
export function safeError(err: unknown, fallback: string): string {
  if (err instanceof ClientApiError) {
    const msg = (err.message || '').trim()
    if (!msg) return fallback
    const lower = msg.toLowerCase()
    if (
      lower.includes('internal') ||
      lower.includes('exception') ||
      lower.includes('traceback') ||
      lower === 'invalid request' ||
      lower.includes('stack') ||
      lower.includes('mt5manager') ||
      lower.includes('pip install') ||
      lower.includes('\\downloads\\') ||
      lower.includes('/downloads/') ||
      lower.includes('.whl') ||
      lower.includes('package is not installed') ||
      lower.includes('metaquotes') ||
      lower.includes('account creation disabled') ||
      lower.includes('mt5_allow') ||
      lower.includes('feature flag')
    ) {
      return fallback
    }
    return msg
  }
  if (err instanceof Error && err.message) {
    const lower = err.message.toLowerCase()
    if (
      lower.includes('internal') ||
      lower.includes('failed to fetch') ||
      lower.includes('mt5manager') ||
      lower.includes('pip install') ||
      lower.includes('package is not installed') ||
      lower.includes('exception')
    ) {
      return fallback
    }
    return err.message
  }
  return fallback
}

export function maskLogin(login: number | string | null | undefined): string {
  const text = String(login ?? '').trim()
  if (!text) return '—'
  if (text.length <= 2) return `**${text}`
  return `***${text.slice(-2)}`
}

export function isSelectableAccount(status?: string | null): boolean {
  const s = String(status || '').toUpperCase()
  return s === 'ACTIVE' || s === ''
}

export function moneySigned(
  value: number | string | null | undefined,
  currency = 'USD',
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(2)} ${currency}`
}

export function formatWhen(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

export function formatDateOnly(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleDateString()
}

export function moneyPlain(
  value: number | string | null | undefined,
  currency = 'USD',
): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  return `${n.toFixed(2)} ${currency}`
}

/** Numeric amount only (no currency) for KPI hierarchy. */
export function moneyAmount(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return '—'
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

/** Client-safe finance type labels (never raw enums). */
export function financeTypeLabel(raw?: string | null): string {
  const s = String(raw || '').toUpperCase()
  if (!s) return '—'
  if (s.includes('OPENING') && s.includes('BALANCE')) return 'Demo Opening Balance'
  if (s.includes('DEMO') && s.includes('DEPOSIT')) return 'Deposit'
  if (s.includes('CRYPTO') && s.includes('DEPOSIT')) return 'Crypto Deposit'
  if (s.includes('WITHDRAW')) return 'Withdrawal'
  if (s.includes('REFUND')) return 'Refund'
  if (s.includes('ADJUST')) return 'Adjustment'
  if (s.includes('DEPOSIT')) return 'Deposit'
  return s
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** MT5 account activity type labels (never imply real money for Demo opening). */
export function activityTypeLabel(raw?: string | null): string {
  const s = String(raw || '').toUpperCase()
  if (!s) return 'Activity'
  if (s.includes('OPENING') && s.includes('BALANCE')) return 'Demo Opening Balance'
  if (s === 'DEMO_OPENING_BALANCE' || s === 'DEMO_OPENING_BALANCE_OR_BALANCE') {
    return 'Demo Opening Balance'
  }
  return s
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

/** Client-safe finance / deposit status labels. */
export function financeStatusLabel(raw?: string | null): string {
  const s = String(raw || '').toUpperCase()
  if (!s) return '—'
  const map: Record<string, string> = {
    AWAITING_PAYMENT: 'Awaiting Payment',
    PENDING: 'Pending',
    PENDING_ADMIN_REVIEW: 'Pending',
    UNDER_REVIEW: 'Pending',
    PAYMENT_CONFIRMED: 'Payment Confirmed',
    PAYMENT_PENDING: 'Awaiting Payment',
    INCOMPLETE_PAYMENT: 'Incomplete Payment',
    PROCESSING: 'Processing',
    PROCESSING_MT5_CREDIT: 'Processing',
    COMPLETED: 'Completed',
    REJECTED: 'Rejected',
    REJECTED_AND_REFUNDED: 'Rejected',
    FAILED: 'Failed',
    EXPIRED: 'Expired',
    RECONCILIATION_REQUIRED: 'Reconciliation Required',
  }
  if (map[s]) return map[s]
  return s
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export function financeStatusTone(raw?: string | null): string {
  const s = String(raw || '').toUpperCase()
  if (/(REJECT|DENIED|FAIL)/.test(s)) return 'rejected'
  if (
    /(COMPLETE|SETTLED|CREDITED|PAID)/.test(s) &&
    !s.includes('PROCESSING') &&
    !s.includes('INCOMPLETE')
  ) {
    return 'completed'
  }
  if (s.includes('INCOMPLETE') || s.includes('EXPIRED') || s.includes('RECONCIL')) return 'recon'
  if (/(CONFIRM)/.test(s)) return 'confirmed'
  if (/(PROCESS|REVIEW)/.test(s)) return 'processing'
  if (/(AWAIT|PEND|INVOICE|SUBMIT|CREATED|OPEN)/.test(s)) return 'awaiting'
  return 'neutral'
}

/** e.g. USDT_TRC20 → USDT · TRC20 */
export function methodNetworkLabel(raw?: string | null): string {
  const s = String(raw || '').trim()
  if (!s) return '—'
  const upper = s.toUpperCase()
  if (upper.includes('USDT') && upper.includes('TRC20')) return 'USDT · TRC20'
  if (upper.includes('UNIWIRE')) return 'Uniwire'
  if (upper.includes('MANUAL') || upper.includes('DEMO')) return 'Manual Demo'
  if (upper.includes('_')) {
    return upper
      .split('_')
      .filter(Boolean)
      .join(' · ')
  }
  return s
}

export async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch {
    return false
  }
}
